import { useEffect, useMemo, useRef, useState } from 'react';
import Button from './ui/Button';
import { searchTobaccosOnline, fetchDrinkRecommendations } from '../lib/pairingService';
import DrinkPairingResults from './DrinkPairingResults';

export default function DrinkPairingBox() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Array<{ id: string; name: string; brand?: string }>>([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [selectedTobaccoId, setSelectedTobaccoId] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [recs, setRecs] = useState<any[]>([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Debounced query for suggestions (online-only)
  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const t = setTimeout(async () => {
      if (query.trim().length < 2) {
        setSuggestions([]);
        return;
      }
      setLoadingSuggest(true);
      try {
        // No caching; direct call each time
        const res = await searchTobaccosOnline(query);
        if (!controller.signal.aborted) setSuggestions(res);
      } finally {
        if (!controller.signal.aborted) setLoadingSuggest(false);
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(t);
    };
  }, [query]);

  async function handleRecommend() {
    if (!selectedTobaccoId) return;
    setLoadingRecs(true);
    try {
      const results = await fetchDrinkRecommendations(selectedTobaccoId);
      setRecs(results);
    } finally {
      setLoadingRecs(false);
    }
  }

  const hasSelection = useMemo(() => !!selectedTobaccoId, [selectedTobaccoId]);

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 600 }}>Pair a Blend</h3>
      </div>
      <div style={{ position: 'relative', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Start typing a tobacco..."
          value={hasSelection ? selectedLabel : query}
          onChange={(e) => {
            setSelectedTobaccoId(null);
            setSelectedLabel('');
            setQuery(e.target.value);
          }}
        />
        {(hasSelection || query.trim().length > 0) && (
          <Button
            variant="secondary"
            size="small"
            onClick={() => {
              setSelectedTobaccoId(null);
              setSelectedLabel('');
              setQuery('');
              setSuggestions([]);
              setRecs([]);
            }}
          >
            Clear
          </Button>
        )}
        {query.trim().length >= 2 && !hasSelection && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'rgba(11,11,11,0.95)',
              border: '1px solid var(--color-border)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              zIndex: 20,
              maxHeight: '220px',
              overflowY: 'auto'
            }}
          >
            {loadingSuggest ? (
              <div style={{ padding: '0.5rem', fontSize: '0.875rem', color: '#999' }}>Searching…</div>
            ) : suggestions.length === 0 ? (
              <div style={{ padding: '0.5rem', fontSize: '0.875rem', color: '#999' }}>No matches</div>
            ) : (
              suggestions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setSelectedTobaccoId(s.id);
                    setSelectedLabel(s.brand ? `${s.brand} • ${s.name}` : s.name);
                    // Clear previous recommendations so next run visibly updates
                    setRecs([]);
                    setSuggestions([]);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    color: 'inherit',
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.06)'
                  }}
                >
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{s.name}</div>
                  {s.brand && <div style={{ fontSize: '0.75rem', color: '#999' }}>{s.brand}</div>}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
        <Button variant="secondary" onClick={handleRecommend} disabled={!selectedTobaccoId} loading={loadingRecs}>
          Recommend
        </Button>
      </div>

      <DrinkPairingResults results={recs} />
    </div>
  );
}


